import React, { useEffect, useState } from 'react';
import { Card, List, Badge, Empty, Button } from 'antd';
import { EditOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';

const PAGE_SIZE = 5;

/**
 * Bekleyen "veli bilgi güncelleme" istekleri.
 *
 * `ParentRequestsPendingPanel` (devamsızlık/telafi) ile AYRI panel — o
 * öğretmen panosunda da var, bu yalnızca yöneticide. İstek metni velinin
 * e-posta/telefon bilgisini taşıyor.
 *
 * Panelin varlık sebebi: eskiden bu istekler yalnızca bildirimdi. Bildirim
 * akışta aşağı kayınca kayboluyor ve yapılıp yapılmadığı hiçbir yerde
 * görünmüyordu — iki yönetici aynı işi ayrı ayrı yapabiliyor ya da ikisi de
 * "diğeri yapmıştır" deyip atlayabiliyordu.
 */
const ParentProfileChangePendingPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [collapsed, setCollapsed] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });

  const canSee =
    ['developer_admin', 'super_admin', 'branch_manager'].includes(user?.role)
    || (user?.permissions || []).includes('users.profile-change-requests');

  const fetchItems = (page = 1) => {
    setLoading(true);
    api.get('/parent-profile-change-requests', { params: { status: 'pending', page, per_page: PAGE_SIZE } })
      .then((res) => {
        setItems(res.data.data);
        setPagination({
          current: res.data.current_page,
          pageSize: res.data.per_page,
          total: res.data.total,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canSee) fetchItems(1);
    else setLoading(false);
  }, [canSee]);

  if (!canSee) return null;

  return (
    <Card
      size="small"
      title={
        <span
          className="cursor-pointer select-none flex items-center gap-2"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <RightOutlined style={{ fontSize: 11, color: '#9ca3af' }} /> : <DownOutlined style={{ fontSize: 11, color: '#9ca3af' }} />}
          <EditOutlined style={{ color: '#fa8c16' }} />
          Veli Bilgi Güncelleme — Bekleyen
          <Badge count={pagination.total} style={{ backgroundColor: pagination.total > 0 ? '#faad14' : '#d9d9d9' }} />
        </span>
      }
      style={{ marginBottom: 16 }}
    >
      {!collapsed && (
        <List
          size="small"
          loading={loading}
          dataSource={items}
          locale={{ emptyText: <Empty description="Bekleyen istek yok." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            size: 'small',
            hideOnSinglePage: true,
            onChange: (page) => fetchItems(page),
          }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  size="small"
                  type="link"
                  onClick={() => navigate(`/users/${item.parent_user_id}?tab=requests`)}
                >
                  Aç
                </Button>,
              ]}
            >
              <div className="w-full">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-medium">
                    {item.parent ? `${item.parent.name} ${item.parent.surname}` : `#${item.parent_user_id}`}
                  </span>
                  <span className="text-gray-400 text-xs">
                    {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                  </span>
                </div>
                {/* Tek satır: panoda yer kaplamasın, tamamı "Aç" ile okunuyor. */}
                <div className="text-gray-600 text-sm truncate">{item.message}</div>
              </div>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default ParentProfileChangePendingPanel;
